import { useCallback, useEffect, useSyncExternalStore } from "react"
import {
  getVoximplantClient,
  type VoximplantClient,
} from "@/lib/voice/voximplant-client"
import type {
  SoftphoneState,
  VoiceConfig,
  VoiceCallRecord,
} from "@/types/v2/voice"

export interface UseVoximplant {
  state: SoftphoneState
  answer: () => void
  decline: () => void
  hangup: () => void
  toggleMute: () => void
  toggleHold: () => void
  sendTone: (tone: string) => void
  reconnect: () => void
  selectMicrophone: (deviceId: string) => void
  placeCall: (destination: string) => void
  applyBroadcast: (voiceCall: VoiceCallRecord) => void
  reconcileActiveCall: (voiceCall: VoiceCallRecord | null) => void
  resumeAudio: () => void
  dismissCall: () => void
}

// Subscribes a component to the module-level Voximplant
// singleton. The singleton is created once and survives
// Inertia navigations; this hook only reads its state
// (via useSyncExternalStore, so browser back/forward and
// concurrent renders stay consistent) and, when given a
// `voiceConfig`, kicks off the one-time connect+login.
//
// Mount this ONCE at the layout level (see `Softphone`),
// never inside a viewport branch — otherwise mobile agents
// never connect and miss inbound calls (CON-82).
export function useVoximplant(
  voiceConfig: VoiceConfig | null,
): UseVoximplant {
  const client: VoximplantClient = getVoximplantClient()

  const state = useSyncExternalStore(
    client.subscribe,
    client.getState,
    client.getState,
  )

  useEffect(() => {
    if (!voiceConfig?.enabled) return
    // start() is idempotent — it connects once per username
    // and no-ops on subsequent mounts / navigations.
    void client.start(voiceConfig).catch(() => {
      // Error already reflected in state.connectionError; the
      // softphone UI surfaces it, so no console output here.
    })
  }, [client, voiceConfig])

  const answer = useCallback(() => void client.answer(), [client])
  const decline = useCallback(() => client.decline(), [client])
  const hangup = useCallback(() => client.hangup(), [client])
  const toggleMute = useCallback(() => client.toggleMute(), [client])
  const toggleHold = useCallback(
    () => void client.toggleHold(),
    [client],
  )
  const sendTone = useCallback(
    (tone: string) => client.sendTone(tone),
    [client],
  )
  const reconnect = useCallback(() => client.reconnect(), [client])
  const selectMicrophone = useCallback(
    (deviceId: string) => client.selectMicrophone(deviceId),
    [client],
  )
  const placeCall = useCallback(
    (destination: string) => void client.placeCall(destination),
    [client],
  )
  const applyBroadcast = useCallback(
    (voiceCall: VoiceCallRecord) => client.applyBroadcast(voiceCall),
    [client],
  )
  const reconcileActiveCall = useCallback(
    (voiceCall: VoiceCallRecord | null) =>
      client.reconcileActiveCall(voiceCall),
    [client],
  )
  const resumeAudio = useCallback(() => client.resumeAudio(), [client])
  const dismissCall = useCallback(() => client.dismissCall(), [client])

  return {
    state,
    answer,
    decline,
    hangup,
    toggleMute,
    toggleHold,
    sendTone,
    reconnect,
    selectMicrophone,
    placeCall,
    applyBroadcast,
    reconcileActiveCall,
    resumeAudio,
    dismissCall,
  }
}
